import { useMemo, useState } from "react";
import {
  Paper,
  Table,
  Stack,
  Group,
  Text,
  Code,
  Badge,
  Button,
  Tooltip,
  ActionIcon,
  SegmentedControl,
  NumberInput,
  TextInput,
  Collapse,
  Alert,
  Box,
  ScrollArea,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconAlertTriangle,
  IconCheck,
  IconArrowBackUp,
  IconCircleCheck,
  IconReceiptTax,
  IconPhotoOff,
} from "@tabler/icons-react";
import { fmtMoney } from "./helpers";

// Match tolerance when comparing invoice gross vs GR gross. We accept up to
// 0.5% relative drift OR 0.01 absolute (rounding from VAT × quantity).
const MATCH_REL_TOL = 0.005;
const MATCH_ABS_TOL = 0.01;
function pricesEffectivelyMatch(a, b) {
  if (a === null || a === undefined || b === null || b === undefined)
    return false;
  const da = Number(a);
  const db = Number(b);
  if (!Number.isFinite(da) || !Number.isFinite(db)) return false;
  const diff = Math.abs(da - db);
  if (diff <= MATCH_ABS_TOL) return true;
  const base = Math.max(Math.abs(da), Math.abs(db), 1e-6);
  return diff / base <= MATCH_REL_TOL;
}
function fmtPct(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toFixed(2).replace(/\.?0+$/, "")}%`;
}

const MISMATCH_COLORS = {
  quantity: "yellow",
  price: "yellow",
  both: "orange",
  unmatched: "red",
  unreadable: "red",
  image_error: "red",
};

const MISMATCH_LABELS = {
  image_error: "image error",
};

function mismatchLabel(type) {
  return MISMATCH_LABELS[type] || type || "mismatch";
}

/**
 * Compact tabular line view with a filter toggle (all vs. mismatched) and an
 * inline expandable resolution editor.
 *
 * Mutations are owned by the parent; we just call `onResolve(lineId, body)`
 * and `onUnresolve(lineId)` which both go to the server immediately. There is
 * no client-side draft state besides the inline Custom inputs while expanded.
 */
export function LinesTable({
  invoice,
  locked,
  onResolve,
  onUnresolve,
  resolvingLineId,
  unresolvingLineId,
}) {
  const [filter, setFilter] = useState("all"); // "all" | "mismatched"
  const [expandedId, setExpandedId] = useState(null);

  const lines = invoice.lines || [];
  const counts = useMemo(() => {
    let mismatched = 0;
    let unresolved = 0;
    let resolved = 0;
    let complete = 0;
    for (const l of lines) {
      if (l.kind === "mismatched") {
        mismatched++;
        if (l.isResolved) resolved++;
        else unresolved++;
      } else {
        complete++;
      }
    }
    return { mismatched, unresolved, resolved, complete };
  }, [lines]);

  const visible = useMemo(() => {
    if (filter === "mismatched") return lines.filter((l) => l.kind === "mismatched");
    return lines;
  }, [lines, filter]);

  return (
    <Paper withBorder p={0}>
      <Group justify="space-between" p="sm" wrap="wrap">
        <Group gap="xs">
          <Text fw={600}>Lines</Text>
          <Badge variant="light" color="gray">
            {lines.length} total
          </Badge>
          <Badge variant="light" color="green" leftSection={<IconCircleCheck size={12} />}>
            {counts.complete} complete
          </Badge>
          {counts.resolved > 0 && (
            <Badge variant="light" color="teal" leftSection={<IconCheck size={12} />}>
              {counts.resolved} resolved
            </Badge>
          )}
          {counts.unresolved > 0 && (
            <Badge
              variant="filled"
              color="orange"
              leftSection={<IconAlertTriangle size={12} />}
            >
              {counts.unresolved} unresolved
            </Badge>
          )}
        </Group>
        <SegmentedControl
          size="xs"
          value={filter}
          onChange={setFilter}
          data={[
            { value: "all", label: `All (${lines.length})` },
            { value: "mismatched", label: `Mismatched only (${counts.mismatched})` },
          ]}
        />
      </Group>

      {locked && (
        <Alert m="sm" color="blue" icon={<IconCircleCheck size={16} />} variant="light">
          Invoice is {invoice.pushedToSap ? "posted to SAP" : "marked ready"} —
          lines are read-only.
        </Alert>
      )}

      <ScrollArea>
        <Table verticalSpacing="xs" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 30 }} />
              <Table.Th style={{ width: 50 }}>#</Table.Th>
              <Table.Th>Item</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th ta="right">Duvo invoice</Table.Th>
              <Table.Th ta="right">GR ref (payload)</Table.Th>
              <Table.Th ta="right">SAP snapshot</Table.Th>
              <Table.Th ta="right">Resolved</Table.Th>
              <Table.Th style={{ width: 1 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {visible.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={10}>
                  <Text ta="center" c="dimmed" size="sm" py="md">
                    {filter === "mismatched"
                      ? "No mismatched lines on this invoice."
                      : "No lines."}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              visible.map((line) => {
                const isMismatched = line.kind === "mismatched";
                const needsAttention = isMismatched && !line.isResolved;
                const isExpanded = expandedId === line.id;
                const canExpand = !locked && isMismatched;
                const rowStyle = needsAttention
                  ? {
                      borderLeft: "3px solid var(--mantine-color-orange-6)",
                      background: "var(--mantine-color-orange-0)",
                    }
                  : line.isResolved
                  ? { borderLeft: "3px solid var(--mantine-color-teal-6)" }
                  : undefined;
                const mainRow = (
                  <Table.Tr
                    key={`${line.id}-row`}
                    style={{
                      cursor: canExpand ? "pointer" : "default",
                      ...rowStyle,
                    }}
                    onClick={() => {
                      if (canExpand) {
                        setExpandedId(isExpanded ? null : line.id);
                      }
                    }}
                  >
                      <Table.Td>
                        {canExpand ? (
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="gray"
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? (
                              <IconChevronDown size={14} />
                            ) : (
                              <IconChevronRight size={14} />
                            )}
                          </ActionIcon>
                        ) : null}
                      </Table.Td>
                      <Table.Td>
                        <Code>{line.lineNum}</Code>
                      </Table.Td>
                      <Table.Td style={{ minWidth: 240 }}>
                        <Group gap={6} wrap="nowrap" align="flex-start">
                          {line.itemCode ? (
                            <Code style={{ flexShrink: 0 }}>{line.itemCode}</Code>
                          ) : (
                            <Badge color="red" size="xs" variant="light">
                              no item
                            </Badge>
                          )}
                          <div style={{ minWidth: 0 }}>
                            <Text size="sm" lineClamp={2}>
                              {line.grItemDescription || "—"}
                            </Text>
                            {line.grItemDescriptionAr && (
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {line.grItemDescriptionAr}
                              </Text>
                            )}
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge line={line} />
                      </Table.Td>
                      <Table.Td ta="right">
                        <ValueCell qty={line.invoiceQuantity} price={line.invoiceUnitPrice} />
                      </Table.Td>
                      <Table.Td ta="right">
                        <ValueCell
                          qty={line.grQuantityPayload}
                          price={line.grReferencePricePayload}
                          vatPercent={line.grVatPercentSap}
                          grossPrice={
                            line.grPriceWithVatSap ??
                            computeGross(
                              line.grReferencePricePayload,
                              line.grVatPercentSap
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td ta="right">
                        <ValueCell
                          qty={line.grQuantitySap}
                          price={line.grPriceSap}
                          vatPercent={line.grVatPercentSap}
                          grossPrice={line.grPriceWithVatSap}
                        />
                      </Table.Td>
                      <Table.Td ta="right">
                        {line.isResolved ? (
                          <ValueCell
                            qty={line.resolvedQuantity}
                            price={line.resolvedUnitPrice}
                            highlight
                          />
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {line.isResolved && !locked && (
                          <Tooltip label="Undo resolution">
                            <ActionIcon
                              variant="subtle"
                              color="orange"
                              loading={unresolvingLineId === line.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                onUnresolve?.(line.id);
                              }}
                              aria-label="Undo resolution"
                            >
                              <IconArrowBackUp size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Table.Td>
                  </Table.Tr>
                );
                const expandRow = canExpand ? (
                  <Table.Tr key={`${line.id}-expand`}>
                    <Table.Td colSpan={9} style={{ padding: 0 }}>
                      <Collapse in={isExpanded}>
                        <Box p="sm" bg="var(--mantine-color-gray-0)">
                          <ResolveEditor
                            line={line}
                            pending={resolvingLineId === line.id}
                            onResolve={(body) => {
                              onResolve?.(line.id, body);
                              setExpandedId(null);
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Table.Td>
                  </Table.Tr>
                ) : null;
                return expandRow ? [mainRow, expandRow] : mainRow;
              })
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <Group p="sm" justify="flex-end">
        <Text size="xs" c="dimmed">
          Each action is saved to the database immediately.
        </Text>
      </Group>
    </Paper>
  );
}

function StatusBadge({ line }) {
  if (line.kind !== "mismatched") {
    return (
      <Badge color="green" size="sm" variant="light">
        complete
      </Badge>
    );
  }
  if (line.isResolved) {
    const tip = line.mismatchType
      ? `Originally mismatched: ${mismatchLabel(line.mismatchType)}${
          line.resolutionNote ? ` — ${line.resolutionNote}` : ""
        }`
      : line.resolutionNote || "Resolved";
    return (
      <Tooltip label={tip} multiline w={260}>
        <Badge
          color="teal"
          size="sm"
          variant="light"
          leftSection={<IconCheck size={12} />}
        >
          resolved{line.mismatchType ? ` · ${mismatchLabel(line.mismatchType)}` : ""}
        </Badge>
      </Tooltip>
    );
  }
  const isImageErr = line.mismatchType === "image_error";
  return (
    <Badge
      color={MISMATCH_COLORS[line.mismatchType] || "orange"}
      size="sm"
      variant="filled"
      leftSection={
        isImageErr ? <IconPhotoOff size={12} /> : <IconAlertTriangle size={12} />
      }
    >
      {mismatchLabel(line.mismatchType)}
    </Badge>
  );
}

function computeGross(net, vatPercent) {
  if (net === null || net === undefined) return null;
  const v = Number(vatPercent);
  if (!Number.isFinite(v) || v <= 0) return null;
  return Number(net) * (1 + v / 100);
}

function ValueCell({ qty, price, highlight, vatPercent, grossPrice }) {
  const qNull = qty === null || qty === undefined;
  const pNull = price === null || price === undefined;
  if (qNull && pNull) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  const color = highlight ? "teal.7" : undefined;
  const vat = Number(vatPercent);
  const hasTax = Number.isFinite(vat) && vat > 0;
  const gross =
    grossPrice ?? (hasTax ? computeGross(price, vatPercent) : null);
  return (
    <Stack gap={1} align="flex-end">
      <Group gap={4} justify="flex-end" align="baseline" wrap="nowrap">
        <Text size="sm" fw={highlight ? 700 : 500} c={color}>
          {qNull ? "—" : fmtMoney(qty, 2)}
        </Text>
        <Text size="xs" c="dimmed">
          ×
        </Text>
        <Text size="sm" fw={highlight ? 600 : 400} c={color}>
          {pNull ? "—" : fmtMoney(price, 2)}
        </Text>
      </Group>
      {hasTax && gross !== null && (
        <Tooltip
          withArrow
          multiline
          w={240}
          label={`Net unit price ${fmtMoney(price, 4)} + ${fmtPct(
            vatPercent
          )} VAT = gross ${fmtMoney(gross, 4)} (what the invoice prints).`}
        >
          <Badge
            color="grape"
            variant="light"
            size="xs"
            leftSection={<IconReceiptTax size={10} />}
            style={{ textTransform: "none", cursor: "help" }}
          >
            +{fmtPct(vatPercent)} = {fmtMoney(gross, 2)}
          </Badge>
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * Type-aware resolution editor.
 *
 *   mismatchType='price'    →  two single-line buttons: Price Duvo / GR
 *   mismatchType='quantity' →  two single-line buttons: Qty Duvo / GR
 *   mismatchType='both'     →  two pickers (qty + price), each click commits
 *                              instantly via auto-apply when the other axis
 *                              is already set
 *   mismatchType='unmatched'→  Use GR values
 *   anything else / fallback→  Use Duvo / Use GR
 *
 * A compact, always-visible "Override" row at the bottom lets the user type
 * in a different qty / price + optional note and Save. No show/hide toggle.
 */
function ResolveEditor({ line, pending, onResolve }) {
  const grQty = line.grQuantityPayload ?? line.grQuantitySap;
  const grPrice = line.grReferencePricePayload ?? line.grPriceSap;
  const invQty = line.invoiceQuantity;
  const invPrice = line.invoiceUnitPrice;

  const submit = (resolvedQty, resolvedPrice, note) => {
    if (pending) return;
    onResolve({
      resolvedQuantity: Number(resolvedQty ?? 0),
      resolvedUnitPrice: Number(resolvedPrice ?? 0),
      resolutionNote: note || null,
    });
  };

  const type = line.mismatchType;
  let header;
  let quick;
  if (type === "price") {
    header = (
      <span>
        Quantity matches ({fmtMoney(invQty)}). Pick the correct{" "}
        <b>unit price</b>:
      </span>
    );
    quick = (
      <>
        <PickButton
          color="blue"
          label="Price · Duvo"
          value={fmtMoney(invPrice)}
          disabled={invPrice === null || invPrice === undefined}
          pending={pending}
          onClick={() => submit(invQty, invPrice, "Used Duvo invoice price")}
        />
        <PickButton
          color="grape"
          label="Price · GR"
          value={fmtMoney(grPrice)}
          disabled={grPrice === null || grPrice === undefined}
          pending={pending}
          onClick={() => submit(invQty, grPrice, "Used GR reference price")}
        />
      </>
    );
  } else if (type === "quantity") {
    header = (
      <span>
        Unit price matches ({fmtMoney(invPrice)}). Pick the correct{" "}
        <b>quantity</b>:
      </span>
    );
    quick = (
      <>
        <PickButton
          color="blue"
          label="Qty · Duvo"
          value={fmtMoney(invQty)}
          disabled={invQty === null || invQty === undefined}
          pending={pending}
          onClick={() => submit(invQty, invPrice, "Used Duvo invoice quantity")}
        />
        <PickButton
          color="grape"
          label="Qty · GR"
          value={fmtMoney(grQty)}
          disabled={grQty === null || grQty === undefined}
          pending={pending}
          onClick={() => submit(grQty, invPrice, "Used GR reference quantity")}
        />
      </>
    );
  } else if (type === "both") {
    return (
      <BothEditor
        line={line}
        invQty={invQty}
        invPrice={invPrice}
        grQty={grQty}
        grPrice={grPrice}
        pending={pending}
        submit={submit}
      />
    );
  } else if (type === "image_error") {
    return (
      <Stack gap={6}>
        <Alert
          color="red"
          variant="light"
          icon={<IconPhotoOff size={16} />}
          title="Image could not be read"
        >
          <Stack gap={4}>
            <Text size="sm">
              The invoice image for this line couldn't be loaded by the OCR
              (network/auth error or corrupted file). The line values can't be
              trusted until a fresh image is supplied.
            </Text>
            <Text size="sm">
              <b>Recommended:</b> request a new invoice image from the supplier
              and have it re-submitted via the inbound endpoint. The new
              submission will overwrite this draft.
            </Text>
            {line.resolutionNote && (
              <Text size="xs" c="dimmed">
                Note from Duvo: {line.resolutionNote}
              </Text>
            )}
          </Stack>
        </Alert>
        <Text size="xs" c="dimmed">
          If you must close this line manually (e.g. supplier confirms values
          by phone), use the override row below to enter the agreed numbers.
        </Text>
        <OverrideRow line={line} pending={pending} submit={submit} />
      </Stack>
    );
  } else if (type === "unmatched") {
    header = <span>Duvo didn't match this line. Use the GR values:</span>;
    quick = (
      <PickButton
        color="grape"
        label="Use GR values"
        value={`${fmtMoney(grQty)} × ${fmtMoney(grPrice)}`}
        disabled={
          (grQty === null || grQty === undefined) &&
          (grPrice === null || grPrice === undefined)
        }
        pending={pending}
        onClick={() => submit(grQty ?? 0, grPrice ?? 0, "Used GR reference values")}
      />
    );
  } else {
    header = <span>Pick the correct values:</span>;
    quick = (
      <>
        <PickButton
          color="blue"
          label="Duvo invoice"
          value={`${fmtMoney(invQty)} × ${fmtMoney(invPrice)}`}
          disabled={invQty === null || invPrice === null}
          pending={pending}
          onClick={() => submit(invQty, invPrice, "Used Duvo invoice values")}
        />
        <PickButton
          color="grape"
          label="GR reference"
          value={`${fmtMoney(grQty)} × ${fmtMoney(grPrice)}`}
          disabled={grQty === null || grPrice === null}
          pending={pending}
          onClick={() => submit(grQty, grPrice, "Used GR reference values")}
        />
      </>
    );
  }

  return (
    <Stack gap={6}>
      <TaxBreakdown line={line} />
      <Text size="xs" c="dimmed" component="div">
        {header}
      </Text>
      <Group gap="xs" wrap="wrap">
        {quick}
      </Group>
      <OverrideRow line={line} pending={pending} submit={submit} />
    </Stack>
  );
}

/**
 * Tax breakdown for the GR side, shown at the top of the expanded resolution
 * row. Surfaces the per-line VAT rate from PDN1 + the gross/net split and a
 * "matches invoice within tolerance" indicator so the AP clerk can see at a
 * glance whether the mismatch is really just a tax-inclusivity artefact.
 */
function TaxBreakdown({ line }) {
  const vat = Number(line.grVatPercentSap);
  const hasTax = Number.isFinite(vat) && vat > 0;
  if (!hasTax) {
    return (
      <Paper p={6} radius="sm" withBorder bg="var(--mantine-color-gray-0)">
        <Group gap="xs">
          <IconReceiptTax size={14} color="var(--mantine-color-gray-6)" />
          <Text size="xs" c="dimmed">
            No VAT on this line — GR price already matches what the invoice
            prints.
          </Text>
        </Group>
      </Paper>
    );
  }
  const net = line.grPriceSap ?? line.grReferencePricePayload;
  const gross =
    line.grPriceWithVatSap ?? computeGross(net, line.grVatPercentSap);
  const invPrice = line.invoiceUnitPrice;
  const matches = pricesEffectivelyMatch(gross, invPrice);
  return (
    <Paper
      p={6}
      radius="sm"
      withBorder
      bg={matches ? "var(--mantine-color-teal-0)" : "var(--mantine-color-gray-0)"}
      style={
        matches
          ? { borderColor: "var(--mantine-color-teal-4)" }
          : undefined
      }
    >
      <Group gap="md" wrap="wrap">
        <Group gap={4} wrap="nowrap">
          <IconReceiptTax size={14} color="var(--mantine-color-grape-6)" />
          <Text size="xs" fw={600}>
            GR tax breakdown
          </Text>
        </Group>
        <BreakdownPair label="Net" value={fmtMoney(net, 4)} />
        <BreakdownPair label="VAT" value={fmtPct(vat)} />
        <BreakdownPair label="Gross" value={fmtMoney(gross, 4)} highlight />
        <BreakdownPair label="Invoice" value={fmtMoney(invPrice, 4)} />
        {matches ? (
          <Badge
            color="teal"
            variant="light"
            size="xs"
            leftSection={<IconCheck size={10} />}
          >
            gross ≈ invoice (within tolerance)
          </Badge>
        ) : (
          <Badge color="orange" variant="light" size="xs">
            still differs after VAT
          </Badge>
        )}
      </Group>
    </Paper>
  );
}

function BreakdownPair({ label, value, highlight }) {
  return (
    <Group gap={4} wrap="nowrap">
      <Text size="xs" c="dimmed">
        {label}:
      </Text>
      <Text size="xs" fw={highlight ? 700 : 500} c={highlight ? "grape.7" : undefined}>
        {value}
      </Text>
    </Group>
  );
}

/**
 * `mismatchType='both'` editor — two single-row selectors that auto-apply
 * the moment both axes are selected. No separate Apply button.
 */
function BothEditor({ line, invQty, invPrice, grQty, grPrice, pending, submit }) {
  const [qtySide, setQtySide] = useState(null);
  const [priceSide, setPriceSide] = useState(null);

  const onPickQty = (side) => {
    setQtySide(side);
    if (priceSide) commit(side, priceSide);
  };
  const onPickPrice = (side) => {
    setPriceSide(side);
    if (qtySide) commit(qtySide, side);
  };
  const commit = (qSide, pSide) => {
    const q = qSide === "invoice" ? invQty : grQty;
    const p = pSide === "invoice" ? invPrice : grPrice;
    const note = `Qty: ${qSide === "invoice" ? "Duvo" : "GR"}, Price: ${
      pSide === "invoice" ? "Duvo" : "GR"
    }`;
    submit(q, p, note);
  };

  return (
    <Stack gap={6}>
      <TaxBreakdown line={line} />
      <Text size="xs" c="dimmed">
        Both differ. Pick one for each — the resolution saves automatically
        when both are picked.
      </Text>
      <SideRow
        label="QTY"
        invValue={invQty}
        grValue={grQty}
        selected={qtySide}
        onSelect={onPickQty}
        disabled={pending}
      />
      <SideRow
        label="PRICE"
        invValue={invPrice}
        grValue={grPrice}
        selected={priceSide}
        onSelect={onPickPrice}
        disabled={pending}
      />
      <OverrideRow line={line} pending={pending} submit={submit} />
    </Stack>
  );
}

// ── Reusable bits ──────────────────────────────────────────────────────────

function PickButton({ color, label, value, disabled, pending, onClick }) {
  return (
    <Button
      size="sm"
      variant="light"
      color={color}
      disabled={disabled || pending}
      loading={pending}
      onClick={onClick}
      style={{ flex: "1 1 180px", minWidth: 140 }}
      styles={{ label: { width: "100%" } }}
    >
      <Group gap={6} justify="space-between" wrap="nowrap" w="100%">
        <Text size="xs" fw={500} style={{ opacity: 0.85 }}>
          {label}
        </Text>
        <Text size="sm" fw={700}>
          {value}
        </Text>
      </Group>
    </Button>
  );
}

function SideRow({ label, invValue, grValue, selected, onSelect, disabled }) {
  return (
    <Group gap="xs" wrap="nowrap" align="center">
      <Text size="xs" fw={700} c="dimmed" w={50}>
        {label}
      </Text>
      <Button
        size="sm"
        variant={selected === "invoice" ? "filled" : "light"}
        color="blue"
        disabled={disabled || invValue === null || invValue === undefined}
        onClick={() => onSelect("invoice")}
        style={{ flex: 1 }}
      >
        Duvo · {fmtMoney(invValue)}
      </Button>
      <Button
        size="sm"
        variant={selected === "gr" ? "filled" : "light"}
        color="grape"
        disabled={disabled || grValue === null || grValue === undefined}
        onClick={() => onSelect("gr")}
        style={{ flex: 1 }}
      >
        GR · {fmtMoney(grValue)}
      </Button>
    </Group>
  );
}

/**
 * Always-visible override row. Compact. Pre-fills from the Duvo invoice
 * values for convenience.
 *
 * When the GR line has VAT > 0, a net/gross toggle is shown. The toggle
 * defaults to "gross" — that's the printed invoice number the AP clerk reads
 * off the supplier paperwork. Under the hood we always submit `net` to the
 * backend (SAP expects net), so when in gross mode we divide by (1 + VAT%).
 */
function OverrideRow({ line, pending, submit }) {
  const vat = Number(line.grVatPercentSap);
  const hasTax = Number.isFinite(vat) && vat > 0;
  // Default to gross when the line has VAT, since invoiceUnitPrice is the
  // gross value Duvo OCRed off the printed supplier invoice. We treat the
  // pre-filled price as already-gross in that case.
  const [basis, setBasis] = useState(hasTax ? "gross" : "net");
  const [qty, setQty] = useState(
    line.invoiceQuantity !== null && line.invoiceQuantity !== undefined
      ? Number(line.invoiceQuantity)
      : ""
  );
  const [price, setPrice] = useState(
    line.invoiceUnitPrice !== null && line.invoiceUnitPrice !== undefined
      ? Number(line.invoiceUnitPrice)
      : ""
  );
  const [note, setNote] = useState("");

  const toNet = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return 0;
    if (basis === "gross" && hasTax) {
      return Math.round((n / (1 + vat / 100)) * 10000) / 10000;
    }
    return n;
  };
  const livePreview = (() => {
    if (!hasTax || price === "" || price === null || price === undefined)
      return null;
    const n = Number(price);
    if (!Number.isFinite(n)) return null;
    if (basis === "gross") {
      const net = n / (1 + vat / 100);
      return `→ net ${fmtMoney(net, 4)} (stored to SAP)`;
    }
    const gross = n * (1 + vat / 100);
    return `→ gross ${fmtMoney(gross, 4)} (invoice print)`;
  })();

  return (
    <Stack gap={4}>
      <Group gap={6} wrap="nowrap" align="center">
        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
          or override:
        </Text>
        <NumberInput
          value={qty}
          onChange={setQty}
          min={0}
          decimalScale={4}
          hideControls
          size="xs"
          w={90}
          placeholder="Qty"
          aria-label="Override quantity"
        />
        <Text size="xs" c="dimmed">
          ×
        </Text>
        <NumberInput
          value={price}
          onChange={setPrice}
          min={0}
          decimalScale={4}
          hideControls
          size="xs"
          w={110}
          placeholder={basis === "gross" ? "Gross price" : "Net price"}
          aria-label="Override unit price"
        />
        {hasTax && (
          <SegmentedControl
            size="xs"
            value={basis}
            onChange={setBasis}
            data={[
              { value: "net", label: "Net" },
              { value: "gross", label: "Gross" },
            ]}
            disabled={pending}
          />
        )}
        <TextInput
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          size="xs"
          placeholder="Note (optional)"
          style={{ flex: 1, minWidth: 140 }}
          aria-label="Resolution note"
        />
        <Button
          size="xs"
          variant="light"
          color="gray"
          loading={pending}
          onClick={() =>
            submit(
              qty || 0,
              toNet(price || 0),
              note ||
                (hasTax && basis === "gross"
                  ? `Custom resolution (entered gross @ ${fmtPct(vat)} VAT)`
                  : "Custom resolution")
            )
          }
        >
          Save
        </Button>
      </Group>
      {livePreview && (
        <Text size="xs" c="dimmed" pl={70}>
          {livePreview}
        </Text>
      )}
    </Stack>
  );
}
